import { InvestorFormFields } from "@/components/investors/InvestorFormFields";
import { PartyUpsertDialog } from "@/components/parties/PartyUpsertDialog";
import { Button } from "@/components/ui/button";
import {
  createInvestor,
  emptyInvestorDraft,
  investorErrorMessage,
  isInvestorDraftComplete,
  validateInvestorDraft,
  type InvestorDraft,
  type InvestorRecord,
} from "@/lib/investors/investorsStore";
import { HandCoins, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (record: InvestorRecord) => void | Promise<void>;
  title?: string;
};

export function CreateInvestorDialog({
  open,
  onOpenChange,
  onCreated,
  title = "Add investor",
}: Props) {
  const [draft, setDraft] = useState<InvestorDraft>(() => emptyInvestorDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(emptyInvestorDraft());
    setSaving(false);
  }, [open]);

  const save = async () => {
    if (saving) return;
    const validation = validateInvestorDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required investor fields.");
      return;
    }
    setSaving(true);
    try {
      const { record, message } = await createInvestor(draft);
      toast.success(message);
      onOpenChange(false);
      await onCreated?.(record);
    } catch (error) {
      toast.error(investorErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartyUpsertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description="Add an investor to your directory with contact and address details."
      icon={HandCoins}
      accent="emerald"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={saving || !isInvestorDraftComplete(draft)}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save investor
          </Button>
        </>
      }
    >
      <InvestorFormFields draft={draft} onChange={setDraft} disabled={saving} />
    </PartyUpsertDialog>
  );
}
